import { ElementRef, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BrowserMultiFormatReader, DecodeHintType, Result } from '@zxing/library';
import { DeviceDetectorService } from 'ngx-device-detector';
import { BehaviorSubject, from, Observable, Subject } from 'rxjs';
import { filter, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { CodeScannerDialogComponent } from '../components/code-scanner/code-scanner-dialog.component';
import { CodeScannerDialogResponse, FacingMode } from './code-scanner.model';

export const VIDEO_DEVICE_TYPE = 'videoinput';

/**
 * This service wraps whole responsibility for MediaDevices API.
 * Please, do not modify workflow with devices outside this service, as their order matters.
 */
@Injectable({
  providedIn: 'root',
})
export class CodeScannerService {
  private stopStream$: Subject<void> = new Subject<void>();
  private facingMode$: BehaviorSubject<FacingMode> = new BehaviorSubject<FacingMode>(
    FacingMode.environment
  );

  public isMoreCamerasAvailable$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public isCameraReady$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  public track: MediaStreamTrack;
  public permissionException = new BehaviorSubject(false);
  public isCodeScannerAvailable$ = this.isCodeScannerAvailable();

  constructor(private dialog: MatDialog, private deviceService: DeviceDetectorService) {}

  /**
   * This opens dialog with component responsible for html video element
   */
  openCodeDialog(): Observable<CodeScannerDialogResponse> {
    return this.dialog
      .open(CodeScannerDialogComponent, {
        width: '535px',
        panelClass: 'qr-scanner-dialog',
        disableClose: true,
      })
      .afterClosed()
      .pipe(filter<CodeScannerDialogResponse>(Boolean));
  }

  /**
   * Manages access to cameras, returns received video stream.
   * ORDER OF EXECUTED ACTIONS IS IMPORTANT.
   * Do not enumerateDevices before getUserMedia!
   */
  initStream(): Observable<MediaStream> {
    return this.facingMode$.pipe(
      tap(() => {
        this.track?.stop();
      }),
      switchMap((facingMode) =>
        from(
          navigator.mediaDevices.getUserMedia({
            video: {
              facingMode,
              width: 2560,
              height: 1440,
            },
          })
        )
      ),
      tap((stream) => {
        this.isCameraReady$.next(true);
        this.track = stream.getVideoTracks()[0];
        this.fetchCameraIds()
          .pipe(map((cameraIds) => cameraIds.length > 1))
          .subscribe((isMoreCameras) => this.isMoreCamerasAvailable$.next(isMoreCameras));
      })
    );
  }

  /**
   * Loop responsible for triggering action which is looking for QR code on video snapshot
   *
   * @param video html element
   */
  scanOnVideo(video: ElementRef): Observable<string> {
    return this.scanCode(video).pipe(
      map((result) => result.getText()),
      takeUntil(this.stopStream$)
    );
  }

  /**
   * Triggers change between facing mode.
   * Should be executed only if isMoreCamerasAvailable$
   */
  switchCamera(): void {
    this.isCameraReady$.next(false);
    this.facingMode$.next(this.getNextFacingMode(this.facingMode$.value));
  }

  /**
   * Used onDestroy to finish all started actions.
   */
  endStream(): void {
    this.stopStream$.next();
    this.track?.stop();
  }

  private getNextFacingMode(current: FacingMode): FacingMode {
    const facingModes = [FacingMode.environment, FacingMode.user];
    const index = facingModes.indexOf(current);

    return facingModes[(index + 1) % facingModes.length];
  }

  private isCodeScannerAvailable(): Observable<boolean> {
    const isMobileDevice = this.deviceService.isMobile() || this.deviceService.isTablet();

    return this.permissionException.pipe(
      map((permissionException) => !permissionException && isMobileDevice)
    );
  }

  private fetchCameraIds(): Observable<string[]> {
    return from(navigator.mediaDevices.enumerateDevices()).pipe(
      take(1),
      map((devices) => devices.filter((device) => device.kind === VIDEO_DEVICE_TYPE)),
      map((cameras) => cameras.map((camera) => camera.deviceId))
    );
  }

  private scanCode(video: ElementRef<HTMLVideoElement>): Observable<Result> {
    const hints = new Map();
    hints.set(DecodeHintType.ASSUME_GS1, true);

    const codeReader = new BrowserMultiFormatReader(hints);
    codeReader.timeBetweenDecodingAttempts = 500;

    return new Observable((subscriber) => {
      codeReader.decodeFromVideoElementContinuously(video.nativeElement, (res) => {
        if (!!res) {
          subscriber.next(res);
          subscriber.complete();
        }
      });

      return {
        unsubscribe(): void {
          codeReader.stopContinuousDecode();
        },
      };
    });
  }
}
